import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ImagePlus, LayoutTemplate, Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  Callout, Checkbox, ConfirmModal, FormField, IconButton, Input, NativeSelect, StatusBadge, Textarea,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import apiClient from "../../utils/apiClient";
import { useBranding } from "../../contexts/BrandingContext";
import useBrandMark from "../../hooks/useBrandMark";
import { AuthBrandPanel, LOGIN_PAGE_DEFAULTS, headlineNode } from "../Auth/AuthBrandSide";
import { LOGIN_PAGE_ICONS, iconFor } from "../Auth/loginPageIcons";
import defaultHero from "../../Assets/Images/auth_bg.png";

// Mirrors vc_be/utils/loginPageContent LIMITS until the server's arrive with GET.
const FALLBACK_LIMITS = {
  kicker: 40, headline: 80, headlineAccent: 40, blurb: 280, footnote: 160,
  highlights: 4, highlightTitle: 40, highlightDesc: 100, stats: 3, statValue: 12, statLabel: 30,
};

const OVERLAY_OPTIONS = [
  { value: "light", label: "Light — more of the photo shows" },
  { value: "medium", label: "Balanced (recommended)" },
  { value: "strong", label: "Strong — text stands out most" },
];

const ICON_OPTIONS = Object.entries(LOGIN_PAGE_ICONS).map(([value, { label }]) => ({ value, label }));

/** The editable part of the server's `loginPage` (no background key, no flags). */
const toForm = (lp = {}) => ({
  enabled: lp.enabled === true,
  kicker: lp.kicker || "",
  headline: lp.headline || "",
  headlineAccent: lp.headlineAccent || "",
  blurb: lp.blurb || "",
  footnote: lp.footnote || "",
  highlights: (lp.highlights || []).map((h) => ({ ...h })),
  stats: (lp.stats || []).map((s) => ({ ...s })),
  overlay: lp.overlay || "medium",
});

/** Today's default panel, as an editable starting point. */
const defaultsForm = (enabled) => ({
  ...toForm(LOGIN_PAGE_DEFAULTS),
  enabled,
});

/** A field's "12 / 40" counter, as the field's hint. */
const counter = (value, max) => `${String(value || "").length} / ${max}`;

/**
 * The real login panel, drawn at desktop size and scaled to fit the column —
 * so what the admin sees is exactly what a student sees, not an approximation.
 */
const PANEL_W = 680;
const PANEL_H = 780;

function LoginPagePreview({ form, backgroundUrl }) {
  const mark = useBrandMark();
  const boxRef = useRef(null);
  const [scale, setScale] = useState(0.5);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const fit = () => setScale(Math.min(1, (el.clientWidth || PANEL_W * 0.5) / PANEL_W));
    fit();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const content = form.enabled
    ? {
        kicker: form.kicker,
        title: headlineNode(form.headline, form.headlineAccent),
        blurb: form.blurb,
        highlights: form.highlights,
        stats: form.stats,
        footnote: form.footnote,
        background: backgroundUrl || defaultHero,
        overlay: form.overlay,
      }
    : {
        title: headlineNode(LOGIN_PAGE_DEFAULTS.headline, LOGIN_PAGE_DEFAULTS.headlineAccent),
        blurb: LOGIN_PAGE_DEFAULTS.blurb,
        highlights: LOGIN_PAGE_DEFAULTS.highlights,
        footnote: LOGIN_PAGE_DEFAULTS.footnote,
        background: defaultHero,
        overlay: "medium",
      };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-muted">
        Preview
      </span>
      <div
        ref={boxRef}
        className="tw-relative tw-w-full tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-line"
        style={{ height: PANEL_H * scale }}
      >
        <div
          className="tw-absolute tw-left-0 tw-top-0 tw-origin-top-left"
          style={{ width: PANEL_W, height: PANEL_H, transform: `scale(${scale})` }}
        >
          <AuthBrandPanel mark={mark} preview {...content} />
        </div>
      </div>
      <span className="tw-text-xs tw-text-content-muted">
        Shown beside the sign-in, sign-up and forgot-password forms on screens wider than a tablet.
      </span>
    </div>
  );
}

/**
 * Settings → Branding → Login page.
 *
 * The left half of the institute's sign-in screens: its own headline, words,
 * highlight tiles, figures and photograph instead of ours. Off by default; when
 * switched on the form starts from today's panel, so an admin edits rather than
 * starts from a blank page. Everything is plain text with limits — the panel is
 * shown to people who are not signed in.
 */
export default function LoginPageSettings({ showToast }) {
  const { refreshBranding } = useBranding();
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(defaultsForm(false));
  const [limits, setLimits] = useState(FALLBACK_LIMITS);
  const [backgroundUrl, setBackgroundUrl] = useState(null);
  const [busy, setBusy] = useState(null); // 'save' | 'upload' | 'remove'
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const apply = (data) => {
    const next = toForm(data.loginPage);
    // Never saved yet → start from today's panel, switched off.
    const fresh = !next.enabled && !next.headline && !next.blurb && !next.highlights.length;
    setForm(fresh ? defaultsForm(false) : next);
    setSaved(data.loginPage);
    setBackgroundUrl(data.backgroundUrl || null);
    if (data.limits) setLimits({ ...FALLBACK_LIMITS, ...data.limits });
  };

  useEffect(() => {
    let live = true;
    apiClient.get("/institute/login-page")
      .then(({ data }) => { if (live) apply(data); })
      .catch(() => { if (live) setError("Could not load your login page settings."); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setRow = (listKey, i, patch) =>
    setForm((f) => ({ ...f, [listKey]: f[listKey].map((r, j) => (j === i ? { ...r, ...patch } : r)) }));
  const removeRow = (listKey, i) => setForm((f) => ({ ...f, [listKey]: f[listKey].filter((_, j) => j !== i) }));

  const save = async () => {
    if (form.enabled && !form.headline.trim()) {
      setError("Add a headline before switching on your own login page.");
      return;
    }
    setError("");
    setBusy("save");
    try {
      const { data } = await apiClient.put("/institute/login-page", { loginPage: form });
      apply(data);
      refreshBranding?.();
      showToast?.(form.enabled ? "Your login page is live." : "Login page settings saved.");
    } catch (err) {
      showToast?.(err?.response?.data?.message || "Could not save the login page.", "error");
    } finally {
      setBusy(null);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      showToast?.("Please choose an image under 10 MB.", "error");
      return;
    }
    setBusy("upload");
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await apiClient.post("/institute/login-page/background", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBackgroundUrl(data.backgroundUrl || null);
      setSaved(data.loginPage);
      refreshBranding?.();
      showToast?.("Background photo uploaded.");
    } catch (err) {
      showToast?.(err?.response?.data?.message || "Could not upload the photo.", "error");
    } finally {
      setBusy(null);
    }
  };

  const removeBackground = async () => {
    setConfirmRemove(false);
    setBusy("remove");
    try {
      const { data } = await apiClient.delete("/institute/login-page/background");
      setBackgroundUrl(null);
      setSaved(data.loginPage);
      refreshBranding?.();
      showToast?.("Background photo removed.");
    } catch (err) {
      showToast?.(err?.response?.data?.message || "Could not remove the photo.", "error");
    } finally {
      setBusy(null);
    }
  };

  const live = saved?.enabled === true;

  return (
    <AppCard>
      <AppCardHeader>
        <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
          <div className="tw-min-w-0">
            <AppCardTitle>
              <span className="tw-inline-flex tw-items-center tw-gap-2">
                <LayoutTemplate size={18} aria-hidden="true" /> Login page
              </span>
            </AppCardTitle>
            <AppCardDescription>
              The panel beside your sign-in form. Replace our words and photo with your own — your
              headline, what makes you different, and a photo of your campus or classes.
            </AppCardDescription>
          </div>
          <StatusBadge status={live ? "success" : "neutral"}>{live ? "Your own" : "Default"}</StatusBadge>
        </div>
      </AppCardHeader>

      <AppCardContent>
        {loading ? (
          <Shimmer height={44} count={5} />
        ) : (
          <div className="tw-grid tw-gap-8 xl:tw-grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
            <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-5">
              {error && <Callout tone="danger">{error}</Callout>}

              <Checkbox
                checked={form.enabled}
                onChange={(e) => set({ enabled: e.target.checked })}
                label="Use my own login page content"
                description="Off shows the standard panel. Changes go live when you save."
              />

              <fieldset disabled={!form.enabled} className="tw-m-0 tw-flex tw-min-w-0 tw-flex-col tw-gap-5 tw-border-0 tw-p-0 disabled:tw-opacity-60">
                <section className="tw-flex tw-flex-col tw-gap-4" aria-label="Headline">
                  <FormField label="Small line above the headline" hint={`Optional, e.g. “Admissions open 2026”. ${counter(form.kicker, limits.kicker)}`}>
                    <Input value={form.kicker} maxLength={limits.kicker} onChange={(e) => set({ kicker: e.target.value })} />
                  </FormField>
                  <div className="tw-grid tw-gap-4 md:tw-grid-cols-2">
                    <FormField label="Headline" required hint={`Press Enter once to set it over two lines. ${counter(form.headline, limits.headline)}`}>
                      <Textarea rows={2} value={form.headline} maxLength={limits.headline} onChange={(e) => set({ headline: e.target.value })} />
                    </FormField>
                    <FormField label="Highlighted words" hint={`Shown after the headline in your brand colour. ${counter(form.headlineAccent, limits.headlineAccent)}`}>
                      <Input value={form.headlineAccent} maxLength={limits.headlineAccent} onChange={(e) => set({ headlineAccent: e.target.value })} />
                    </FormField>
                  </div>
                  <FormField label="Description" hint={counter(form.blurb, limits.blurb)}>
                    <Textarea rows={3} value={form.blurb} maxLength={limits.blurb} onChange={(e) => set({ blurb: e.target.value })} />
                  </FormField>
                </section>

                <section className="tw-flex tw-flex-col tw-gap-3" aria-label="Highlights">
                  <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                    <h4 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">Highlights</h4>
                    <span className="tw-text-xs tw-text-content-muted">{form.highlights.length} / {limits.highlights}</span>
                  </div>
                  {form.highlights.map((h, i) => {
                    const Icon = iconFor(h.icon);
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <div key={i} className="tw-grid tw-items-end tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-3 md:tw-grid-cols-[11rem_minmax(0,1fr)_minmax(0,1.4fr)_auto]">
                        <FormField label="Icon">
                          <NativeSelect value={h.icon} onChange={(e) => setRow("highlights", i, { icon: e.target.value })} options={ICON_OPTIONS} />
                        </FormField>
                        <FormField label="Title">
                          <Input value={h.title} maxLength={limits.highlightTitle} onChange={(e) => setRow("highlights", i, { title: e.target.value })} />
                        </FormField>
                        <FormField label="Description">
                          <Input value={h.desc} maxLength={limits.highlightDesc} onChange={(e) => setRow("highlights", i, { desc: e.target.value })} />
                        </FormField>
                        <div className="tw-flex tw-items-center tw-gap-2 tw-pb-0.5">
                          <span className="tw-inline-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-md tw-bg-primary/10 tw-text-primary" aria-hidden="true">
                            <Icon size={18} />
                          </span>
                          <IconButton label={`Remove highlight ${i + 1}`} icon={Trash2} onClick={() => removeRow("highlights", i)} />
                        </div>
                      </div>
                    );
                  })}
                  {form.highlights.length < limits.highlights && (
                    <div>
                      <AppButton variant="outline" size="sm" onClick={() => set({ highlights: [...form.highlights, { icon: "star", title: "", desc: "" }] })}>
                        <Plus size={14} /> Add highlight
                      </AppButton>
                    </div>
                  )}
                </section>

                <section className="tw-flex tw-flex-col tw-gap-3" aria-label="Figures">
                  <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                    <div>
                      <h4 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">Figures</h4>
                      <p className="tw-m-0 tw-text-xs tw-text-content-muted">Optional — e.g. “5,000+ / Students placed”.</p>
                    </div>
                    <span className="tw-text-xs tw-text-content-muted">{form.stats.length} / {limits.stats}</span>
                  </div>
                  {form.stats.map((s, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={i} className="tw-grid tw-items-end tw-gap-3 md:tw-grid-cols-[9rem_minmax(0,1fr)_auto]">
                      <FormField label="Figure">
                        <Input value={s.value} maxLength={limits.statValue} placeholder="5,000+" onChange={(e) => setRow("stats", i, { value: e.target.value })} />
                      </FormField>
                      <FormField label="Label">
                        <Input value={s.label} maxLength={limits.statLabel} placeholder="Students placed" onChange={(e) => setRow("stats", i, { label: e.target.value })} />
                      </FormField>
                      <IconButton label={`Remove figure ${i + 1}`} icon={Trash2} onClick={() => removeRow("stats", i)} />
                    </div>
                  ))}
                  {form.stats.length < limits.stats && (
                    <div>
                      <AppButton variant="outline" size="sm" onClick={() => set({ stats: [...form.stats, { value: "", label: "" }] })}>
                        <Plus size={14} /> Add figure
                      </AppButton>
                    </div>
                  )}
                </section>

                <FormField label="Footnote" hint={`The quiet line at the foot of the panel. ${counter(form.footnote, limits.footnote)}`}>
                  <Input value={form.footnote} maxLength={limits.footnote} onChange={(e) => set({ footnote: e.target.value })} />
                </FormField>

                <section className="tw-flex tw-flex-col tw-gap-3" aria-label="Background photo">
                  <h4 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">Background photo</h4>
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
                    <div
                      className="tw-h-20 tw-w-32 tw-shrink-0 tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-sunken tw-bg-cover tw-bg-center"
                      style={{ backgroundImage: `url(${backgroundUrl || defaultHero})` }}
                      role="img"
                      aria-label={backgroundUrl ? "Your background photo" : "The standard background photo"}
                    />
                    <div className="tw-flex tw-flex-col tw-gap-2">
                      <div className="tw-flex tw-flex-wrap tw-gap-2">
                        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={upload} />
                        <AppButton variant="outline" size="sm" disabled={busy === "upload"} onClick={() => fileRef.current?.click()}>
                          <ImagePlus size={14} /> {busy === "upload" ? "Uploading…" : backgroundUrl ? "Replace photo" : "Upload photo"}
                        </AppButton>
                        {backgroundUrl && (
                          <AppButton variant="ghost" size="sm" disabled={busy === "remove"} onClick={() => setConfirmRemove(true)}>
                            <Trash2 size={14} /> Remove
                          </AppButton>
                        )}
                      </div>
                      <span className="tw-text-xs tw-text-content-muted">
                        A wide landscape photo, at least 1600px across. JPG, PNG or WebP, up to 10 MB.
                      </span>
                    </div>
                  </div>
                  <FormField label="Darken the photo" hint="Keeps the white text readable over a busy or bright photo.">
                    <NativeSelect value={form.overlay} onChange={(e) => set({ overlay: e.target.value })} options={OVERLAY_OPTIONS} />
                  </FormField>
                </section>
              </fieldset>

              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-pt-4">
                <AppButton onClick={save} disabled={busy === "save"}>
                  <Save size={16} /> {busy === "save" ? "Saving…" : "Save login page"}
                </AppButton>
                {form.enabled && (
                  <AppButton variant="ghost" onClick={() => setForm(defaultsForm(true))}>
                    <RotateCcw size={14} /> Start again from the standard copy
                  </AppButton>
                )}
              </div>
            </div>

            <div className="tw-min-w-0 xl:tw-sticky xl:tw-top-4 xl:tw-self-start">
              <LoginPagePreview form={form} backgroundUrl={backgroundUrl} />
            </div>
          </div>
        )}
      </AppCardContent>

      <ConfirmModal
        show={confirmRemove}
        onHide={() => setConfirmRemove(false)}
        onConfirm={removeBackground}
        title="Remove the background photo?"
        message="The login page goes back to the standard photo."
        confirmText="Remove"
        variant="danger"
      />
    </AppCard>
  );
}
